/**
 * The headline figures of the costing workbench (batch 8 T11, #3032).
 *
 * Four different things are kept apart on purpose, because they need different actions:
 *  - unconfirmed: layers exist and their cost is unknown (confirm them);
 *  - unbacked: stock exists and no layer backs it (reconcile creates an unknown-cost layer);
 *  - over-backed: layers hold more units than the stock does (shown, never repaired here);
 *  - unbacked consumed: units that left with no layer behind them (cost stays unknown, nothing here costs them).
 * The known value is the value of costed units only; it is hidden without financial read access.
 */
import { Alert, Box, Paper, Skeleton, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { describeApiError } from '@/lib/describeApiError';
import type { CostingSummary } from '@/hooks/useInventoryCosting';
import { formatEuroFromCents, formatEuroFromCentsText } from './costMoney';

export interface CostingSummaryCardsProps {
  readonly summary: CostingSummary | undefined;
  readonly loading: boolean;
  readonly error: unknown;
}

function Card(props: { label: string; value: string; caption: string; tone?: 'warning.main' | 'error.main' | 'info.main' | 'success.main' | 'text.primary'; testId: string }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, flex: '1 1 200px', minWidth: 0 }} data-testid={props.testId}>
      <Typography variant="caption" color="text.secondary">
        {props.label}
      </Typography>
      <Typography variant="h5" sx={{ color: props.tone ?? 'text.primary', wordBreak: 'break-word' }}>
        {props.value}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {props.caption}
      </Typography>
    </Paper>
  );
}

export function CostingSummaryCards({ summary, loading, error }: CostingSummaryCardsProps) {
  const { t, i18n } = useTranslation('inventoryCosting');
  const { t: tc } = useTranslation('common');
  const language = i18n.language;

  if (error) {
    return (
      <Alert severity="error" role="alert">
        {describeApiError(error, tc, { codePrefix: 'inventoryCosting:errors', fallback: t('summary.loadFailed') })}
      </Alert>
    );
  }
  if (!summary) {
    return loading ? <Skeleton variant="rounded" height={96} aria-label={tc('loading')} /> : null;
  }

  const value = summary.value
    ? formatEuroFromCents(summary.value.cents, language)
    : t('summary.valueHidden');
  const exact = summary.value ? formatEuroFromCentsText(summary.value.cents4, language) : null;
  return (
    <Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
        <Card
          testId="summary-known-value"
          label={t('summary.knownValue')}
          value={value}
          caption={
            summary.value
              ? exact !== null && exact !== value
                ? t('summary.knownValueExact', { exact, quantity: summary.quantities.known })
                : t('summary.knownValueUnits', { quantity: summary.quantities.known })
              : t('summary.valueHiddenHint')
          }
          tone="success.main"
        />
        <Card
          testId="summary-unconfirmed"
          label={t('summary.unconfirmed')}
          value={String(summary.quantities.unconfirmed)}
          caption={t('summary.unconfirmedCaption', { layers: summary.openingLayers.rootCount, consumed: summary.openingLayers.consumedQuantity })}
          tone={summary.quantities.unconfirmed > 0 ? 'warning.main' : 'text.primary'}
        />
        <Card
          testId="summary-unbacked"
          label={t('summary.unbacked')}
          value={String(summary.quantities.unbacked)}
          caption={t('summary.unbackedCaption', { keys: summary.keys.unbacked })}
          tone={summary.quantities.unbacked > 0 ? 'warning.main' : 'text.primary'}
        />
        <Card
          testId="summary-over-backed"
          label={t('summary.overBacked')}
          value={String(summary.quantities.overBacked)}
          caption={t('summary.overBackedCaption', { keys: summary.keys.overBacked })}
          tone={summary.quantities.overBacked > 0 ? 'info.main' : 'text.primary'}
        />
        <Card
          testId="summary-unbacked-consumed"
          label={t('summary.unbackedConsumed')}
          value={String(summary.quantities.unbackedConsumed)}
          caption={t('summary.unbackedConsumedCaption')}
          tone={summary.quantities.unbackedConsumed > 0 ? 'error.main' : 'text.primary'}
        />
      </Box>
      {!summary.valueIsComplete && (
        <Alert severity="info" sx={{ mt: 2 }}>
          {t('summary.incomplete')}
        </Alert>
      )}
    </Box>
  );
}
